<template>
  <RevSkeleton
    v-if="!hasFetchedOnce"
    height="200px"
    shape="rectangle"
    width="100%"
  />
  <ProfileCard
    v-else
    data-qa="shipping-address-card"
    :title="i18n(translations.header)"
    @edit="openModal(modalName)"
  >
    <AddressComponent
      v-if="hasAddress"
      :address="displayAddress"
      localize-name-order
      with-details
      with-phone
    />
    <p v-else data-test="empty-state">
      {{ i18n(translations.emptyAddress) }}
    </p>
  </ProfileCard>
  <RevDialog
    :name="modalName"
    :title="i18n(translations.header)"
    @close="resetValidationErrors"
  >
    <template #body>
      <AddressForm
        :address="data || null"
        :api-errors
        :country-options
        :is-loading="isFormLoading"
        show-phone
        @submit="updateAddress"
      />
    </template>
  </RevDialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import {
  getShippingAddress,
  postShippingAddress,
} from '@backmarket/http-api/src/api-specs-after-sale-experience/client/shipping-address'
import { HttpEvent } from '@backmarket/http-api/src/utils/types'
import AddressComponent from '@backmarket/nuxt-module-address/Address.vue'
import type { Address } from '@backmarket/nuxt-module-address/address'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useSWRAsyncData } from '@backmarket/nuxt-module-http-v2/useSWRAsyncData'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevDialog } from '@ds/components/Dialog'
import { getPhoneNumberInfos } from '@ds/components/InputPhone'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevSkeleton } from '@ds/components/Skeleton'

import { SALES_CUSTOMER_CARE_SCOPE } from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '@/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'

import ProfileCard from '../../ProfileCard/ProfileCard.vue'
import type { FormValues } from '../components/AddressForm/AddressForm.types'
import AddressForm from '../components/AddressForm/AddressForm.vue'
import { useMarketplaceCountryCodes } from '../composables/useMarketplaceCountryCodes'
import { useValidationErrors } from '../composables/useValidationErrors'
import { addressIsEmpty } from '../utils/addressIsEmpty'
import { isValidationError } from '../utils/isValidationError'

import translations from './ShippingAddressCard.translations'

const i18n = useI18n()
const { logHttpOnEvent } = useSalesCustomerCareLogger()
const { openErrorToast, openSuccessToast } = useTheToast()
const countryOptions = useMarketplaceCountryCodes()

const modalName = 'PROFILE_SHIPPING_ADDRESS'

const apiErrors = ref<{ [key: string]: string }>({})
const isFormLoading = ref<boolean>(false)

const { data, hasFetchedOnce, refresh } = useSWRAsyncData(
  'shippingAddress',
  () => {
    return $httpFetch(getShippingAddress, {
      onEvent: (event, context) => {
        logHttpOnEvent({
          event,
          context,
          errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myProfile} Error when fetching customer shipping address`,
        })
        if (event === HttpEvent.Fail) {
          if (context.error && context.error.status !== 404) {
            openErrorToast()
          }
        }
      },
    })
  },
  {},
)

const hasAddress = computed(() => !addressIsEmpty(data.value || null))
const displayAddress = computed(() => data.value as unknown as Address)

interface RequestBody extends FormValues {
  countryDialInCode: string
}

function getPhoneProperties(
  formValues: FormValues,
): { countryDialInCode: string; phone: string } | null {
  if (formValues.phone && formValues.country) {
    const { dial, nationalNumber } = getPhoneNumberInfos(
      formValues.phone,
      formValues.country,
    )

    return {
      countryDialInCode: dial,
      phone: nationalNumber,
    }
  }

  return null
}

function transformRequestData(formValues: FormValues): RequestBody {
  return {
    ...(removeEmptyValuesInObject(formValues) as unknown as RequestBody),
    ...(getPhoneProperties(formValues) ?? {}),
  }
}

function resetValidationErrors(): void {
  apiErrors.value = {}
}

// eslint-disable-next-line consistent-return
async function updateAddress(formValues: FormValues) {
  resetValidationErrors()
  isFormLoading.value = true

  try {
    await $httpFetch(postShippingAddress, {
      body: transformRequestData(formValues) as unknown as Record<
        string,
        unknown
      >,
      onEvent: (event, context) =>
        logHttpOnEvent({
          event,
          context,
          errorDetail: {
            request_body: transformRequestData(formValues),
          },
          errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.myProfile} Error when updating customer shipping address`,
        }),
    })
    openSuccessToast({ title: i18n(translations.updateSuccess) })
    await refresh()
    closeModal(modalName)
  } catch (err) {
    if (isValidationError(err)) {
      apiErrors.value = useValidationErrors(err.errors, {
        POSTAL_CODE_INVALID: translations.validationErrorPostalCodeInvalid,
        COUNTRY_DIAL_IN_CODE_PHONE_NUMBER_MISMATCH:
          translations.validationErrorPhoneNumberMismatch,
      })
    } else {
      openErrorToast()
    }
  } finally {
    isFormLoading.value = false
  }
}
</script>
